
import React, { useState, useEffect } from 'react';
import { UserProfile, Organization, AcademicYear, ClassRoom, StudentConnection, Message, Announcement, WeeklyLog, Homework, StudentHomeworkStatus, StuckLog, AttendanceRecord, Kudo, TutorLog, InvitationType, InvitationCode, CalendarEvent, ApprovalStatus, AssessmentEvent, AssessmentResult } from '../types';
import { Header, TabMenu, Avatar, WelcomeBanner, TabMenuItem, ProgressRing } from './common';
import { ChatWindow } from './chat';
import { PrincipalHomeworkView } from './homework';
import { StatsView } from './shared';
import { 
    EditProfileModal, InviteParentsModal, BroadcastModal, AssignTeachersModal, 
    StaffApprovalModal, ManageAcademicYearModal, EditOrgModal, 
    CreateEditClassModal, PrincipalCalendarView, PolicyModal 
} from './modals';
import { useTranslate } from '../hooks/useTranslate';
import { HelpModal } from './help';
import { SendKudoModal, SendPrincipalToTeacherKudoModal, PrincipalKudosLog } from './kudos';
import { AssessmentGradingView, BatchPrintView } from './assessments';
import { OrgModulesModal } from './OrgModulesModal';
import { SystemQADiagnosticsModal } from './SystemQADiagnosticsModal';
import { VicePrincipalsModal } from './VicePrincipalsModal';
import { TransferOwnershipModal } from './TransferOwnershipModal';
import { isFeatureEnabled } from '../lib/features';
import { ClassAttendanceRegisterView } from './attendance';

// Define StudentManagementView internally since it's specific to Principal view logic
const StudentManagementView = ({ students, orgClasses, onUpdateStudentStatus, onMoveStudents, onInviteParents, t }: {
    students: StudentConnection[], 
    orgClasses: ClassRoom[], 
    onUpdateStudentStatus: (ids: string[], status: 'removed' | 'graduated') => void, 
    onMoveStudents: (ids: string[], classId: string, yearId: string) => void,
    onInviteParents: () => void,
    t: (key: string) => string
}) => {
    const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
    const [action, setAction] = useState<'move' | 'graduate' | 'remove' | ''>('');
    const [targetClassId, setTargetClassId] = useState('');

    const handleAction = () => {
        if (!action || selectedStudentIds.length === 0) return;
        if (action === 'remove') {
            if (window.confirm(`Remove ${selectedStudentIds.length} students?`)) {
                onUpdateStudentStatus(selectedStudentIds, 'removed');
                setSelectedStudentIds([]);
            }
        } else if (action === 'graduate') {
             if (window.confirm(`Graduate ${selectedStudentIds.length} students?`)) {
                onUpdateStudentStatus(selectedStudentIds, 'graduated');
                setSelectedStudentIds([]);
            }
        } else if (action === 'move' && targetClassId) {
            const targetClass = orgClasses.find(c => c.id === targetClassId);
            if (targetClass) {
                 if (window.confirm(`Move ${selectedStudentIds.length} students to ${targetClass.name}?`)) {
                    onMoveStudents(selectedStudentIds, targetClass.id, targetClass.academicYearId);
                    setSelectedStudentIds([]);
                }
            }
        }
    };

    return (
        <div className="p-4 space-y-4">
            <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border">
                <div>
                    <h3 className="font-bold text-lg">{t('manage_students')}</h3>
                    <p className="text-sm text-gray-500">{t('manage_students_desc')}</p>
                </div>
                <button onClick={onInviteParents} className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2">
                    <span className="material-icons-round">person_add</span> {t('invite_parents')}
                </button>
            </div>

            {selectedStudentIds.length > 0 && (
                <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 flex items-center gap-4 sticky top-0 z-10 shadow-md">
                    <p className="font-bold text-indigo-800">{selectedStudentIds.length} selected</p>
                    <select value={action} onChange={e => setAction(e.target.value as any)} className="p-2 border rounded bg-white">
                        <option value="">Select Action...</option>
                        <option value="move">{t('action_move')}</option>
                        <option value="graduate">{t('action_graduate')}</option>
                        <option value="remove">{t('action_remove')}</option>
                    </select>
                    {action === 'move' && (
                        <select value={targetClassId} onChange={e => setTargetClassId(e.target.value)} className="p-2 border rounded bg-white">
                            <option value="">{t('select_destination_class')}</option>
                            {orgClasses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    )}
                    <button onClick={handleAction} className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-bold">Apply</button>
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="p-3 bg-gray-50 border-b flex items-center gap-3">
                    <input 
                        type="checkbox" 
                        className="w-5 h-5"
                        checked={students.length > 0 && selectedStudentIds.length === students.length}
                        onChange={(e) => setSelectedStudentIds(e.target.checked ? students.map(s => s.id) : [])}
                    />
                    <span className="font-bold text-gray-600">{t('select_all_students')}</span>
                </div>
                {students.map(s => (
                    <div key={s.id} className="p-3 border-b last:border-0 flex items-center gap-3 hover:bg-gray-50">
                        <input 
                            type="checkbox" 
                            className="w-5 h-5"
                            checked={selectedStudentIds.includes(s.id)}
                            onChange={(e) => {
                                if (e.target.checked) setSelectedStudentIds([...selectedStudentIds, s.id]);
                                else setSelectedStudentIds(selectedStudentIds.filter(id => id !== s.id));
                            }}
                        />
                        <Avatar name={s.childName} photoUrl={s.photoUrl} size="sm" />
                        <span className="font-medium">{s.childName}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const AtAGlance = ({ stats, t }: { stats: { activeStudents: number, attendance: number, checkinRate: number, pendingTeachers: number }, t: (key: string) => string }) => (
    <div className="bg-white p-4 rounded-xl shadow-sm border mb-4">
        <h3 className="font-bold text-gray-800 mb-4">{t('at_a_glance')}</h3>
        <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">{stats.activeStudents}</div>
                <div className="text-xs text-gray-500">{t('active_students')}</div>
            </div>
            <div className="text-center relative">
                <div className="w-16 h-16 mx-auto">
                    <ProgressRing percentage={stats.attendance} />
                </div>
                <div className="text-xs text-gray-500 mt-1">{t('todays_attendance')}</div>
            </div>
            <div className="text-center relative">
                <div className="w-16 h-16 mx-auto">
                    <ProgressRing percentage={stats.checkinRate} />
                </div>
                <div className="text-xs text-gray-500 mt-1">{t('weekly_checkin_rate')}</div>
            </div>
        </div>
    </div>
);

const PrincipalClassView = ({ user, cls, org, allStudents, messages, announcements, weeklyLogs, allHomeworks, studentHomeworkStatuses, stuckLogs, allUsers, onBack, setViewingImageUrl, onLogout, onUpdateUser, onUpdateStudentStatus, onMoveStudents, onSendMessage, onUpdateMessage, onDeleteMessage, onUpdateAnnouncement, onDeleteAnnouncement, isArchived, academicYear, destinationClasses, t, tutorLogs, onGenerateInvitationCodes, kudos, onDeleteAccount, assessmentEvents, assessmentResults, onSaveAssessmentEvent, onSaveAssessmentResult, invitationCodes = [], onMarkMessagesRead, attendanceRecords = [], calendarEvents = [] }: { user: UserProfile, cls: ClassRoom, org: Organization, allStudents: StudentConnection[], messages: Message[], announcements: Announcement[], weeklyLogs: WeeklyLog[], allHomeworks: Homework[], studentHomeworkStatuses: StudentHomeworkStatus[], stuckLogs: StuckLog[], allUsers: UserProfile[], onBack: () => void, setViewingImageUrl: (url: string | null) => void, onLogout: () => void, onUpdateUser: (userId: string, data: Partial<UserProfile>) => void, onUpdateStudentStatus: (studentIds: string[], status: 'removed' | 'graduated') => void, onMoveStudents: (studentIds: string[], newClassId: string, newAcademicYearId: string) => void, onSendMessage: (message: Omit<Message, 'id' | 'timestamp' | 'readBy'>) => void, onUpdateMessage: (messageId: string, newText: string, targetLanguage: string) => void, onDeleteMessage: (messageId: string) => void, onUpdateAnnouncement: (announcementId: string, data: Partial<Omit<Announcement, 'id' | 'timestamp'>>) => void, onDeleteAnnouncement: (announcementId: string) => void, isArchived: boolean, academicYear: AcademicYear | undefined, destinationClasses: ClassRoom[], t: (key: string, reps?: any) => string, tutorLogs: TutorLog[], onGenerateInvitationCodes: (type: InvitationType, count: number, orgId: string, classId?: string) => Promise<InvitationCode[]>; kudos: Kudo[]; onDeleteAccount: () => void; assessmentEvents: AssessmentEvent[]; assessmentResults: AssessmentResult[]; onSaveAssessmentEvent: (e: any) => Promise<void>; onSaveAssessmentResult: (r: any) => Promise<void>; invitationCodes?: InvitationCode[]; onMarkMessagesRead?: (messageIds: string[]) => void; attendanceRecords?: AttendanceRecord[]; calendarEvents?: CalendarEvent[]; }) => {
    const [activeTab, setActiveTab] = useState<'communication' | 'student_management' | 'students_stats' | 'homework' | 'approvals'>('communication');
    const [statsSubTab, setStatsSubTab] = useState<'attendance' | 'weekly_checkin'>('attendance');
    const [principalChatMode, setPrincipalChatMode] = useState<'group' | 'direct'>('group');
    const [directSearchQuery, setDirectSearchQuery] = useState('');
    const [directFilter, setDirectFilter] = useState<'all' | 'unread' | 'active'>('all');
    const [viewingStudentDM, setViewingStudentDM] = useState<StudentConnection | null>(null);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    
    // Assessment State
    const [reviewingEvent, setReviewingEvent] = useState<AssessmentEvent | null>(null);
    const [printingEvent, setPrintingEvent] = useState<AssessmentEvent | null>(null);
      
    const classStudents = allStudents.filter(s => s.classId === cls.id && (s.approvalStatus === 'approved' || s.approvalStatus === 'pending'));
    const approvedClassStudents = classStudents.filter(s => s.approvalStatus === 'approved');

    // Auto-mark read when viewing direct messages
    useEffect(() => {
        if (!onMarkMessagesRead) return;
        if (viewingStudentDM) {
            const unreadIds = messages
                .filter(m => m.classId === cls.id && m.studentId === viewingStudentDM.id && m.senderId !== user.id && !(m.readBy || []).includes(user.id))
                .map(m => m.id);
            if (unreadIds.length > 0) {
                onMarkMessagesRead(unreadIds);
            }
        } else if (activeTab === 'communication' && principalChatMode === 'group') {
            const unreadGroupIds = messages
                .filter(m => m.classId === cls.id && !m.studentId && m.senderId !== user.id && !(m.readBy || []).includes(user.id))
                .map(m => m.id);
            if (unreadGroupIds.length > 0) {
                onMarkMessagesRead(unreadGroupIds);
            }
        }
    }, [viewingStudentDM, messages, cls.id, user.id, activeTab, principalChatMode, onMarkMessagesRead]);

    const totalUnreadDirectCount = messages.filter(m => m.classId === cls.id && m.studentId && m.senderId !== user.id && !(m.readBy || []).includes(user.id)).length;

    // Filtered student conversations for direct inquiry bucket
    const studentConversationList = approvedClassStudents.map(student => {
        const parent = allUsers.find(u => u.id === student.parentId);
        const studentDirectMsgs = messages.filter(m => m.classId === cls.id && m.studentId === student.id);
        const unreadCount = studentDirectMsgs.filter(m => m.senderId !== user.id && !(m.readBy || []).includes(user.id)).length;
        const lastMsg = studentDirectMsgs.length > 0 ? studentDirectMsgs[studentDirectMsgs.length - 1] : null;
        const isWaitingForReply = Boolean(lastMsg && (lastMsg.senderRole === 'parent' || lastMsg.senderId === student.parentId));
        return {
            student,
            parent,
            unreadCount,
            lastMsg,
            isWaitingForReply,
            hasMessages: studentDirectMsgs.length > 0
        };
    }).filter(item => {
        if (directSearchQuery.trim()) {
            const q = directSearchQuery.toLowerCase();
            const childMatch = item.student.childName.toLowerCase().includes(q);
            const parentMatch = item.parent?.name?.toLowerCase().includes(q) || false;
            if (!childMatch && !parentMatch) return false;
        }
        if (directFilter === 'unread') {
            return item.unreadCount > 0 || item.isWaitingForReply;
        }
        if (directFilter === 'active') {
            return item.hasMessages;
        }
        return true;
    }).sort((a, b) => {
        if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
        if (b.unreadCount > 0 && a.unreadCount === 0) return 1;
        if (a.isWaitingForReply && !b.isWaitingForReply) return -1;
        if (!a.isWaitingForReply && b.isWaitingForReply) return 1;
        const timeA = a.lastMsg?.timestamp instanceof Date ? a.lastMsg.timestamp.getTime() : (a.lastMsg?.timestamp ? new Date(a.lastMsg.timestamp).getTime() : 0);
        const timeB = b.lastMsg?.timestamp instanceof Date ? b.lastMsg.timestamp.getTime() : (b.lastMsg?.timestamp ? new Date(b.lastMsg.timestamp).getTime() : 0);
        return timeB - timeA;
    });

    const totalNeedsAttentionCount = approvedClassStudents.filter(student => {
        const studentDirectMsgs = messages.filter(m => m.classId === cls.id && m.studentId === student.id);
        const unreadCount = studentDirectMsgs.filter(m => m.senderId !== user.id && !(m.readBy || []).includes(user.id)).length;
        const lastMsg = studentDirectMsgs.length > 0 ? studentDirectMsgs[studentDirectMsgs.length - 1] : null;
        const isWaitingForReply = Boolean(lastMsg && (lastMsg.senderRole === 'parent' || lastMsg.senderId === student.parentId));
        return unreadCount > 0 || isWaitingForReply;
    }).length;

    const handlePrincipalSendMessage = (text: string, originalText: string, imageUrl?: string) => {
        onSendMessage({
            classId: cls.id,
            studentId: viewingStudentDM?.id,
            senderId: user.id,
            senderName: user.name,
            senderRole: 'principal',
            text,
            originalText,
            originalLanguage: user.language,
            imageUrl,
            academicYearId: cls.academicYearId,
        });
    };

    const principalTabs: TabMenuItem[] = [];
    if (isFeatureEnabled(org, 'chat') || isFeatureEnabled(org, 'announcements')) {
        principalTabs.push({ id: 'communication', label: t('communication'), icon: 'forum' });
    }
    principalTabs.push({ id: 'approvals', label: t('approvals'), icon: 'rule' });
    if (isFeatureEnabled(org, 'homework')) {
        principalTabs.push({ id: 'homework', label: t('homework'), icon: 'home_work' });
    }
    if (isFeatureEnabled(org, 'attendance') || isFeatureEnabled(org, 'weeklyCheckin')) {
        principalTabs.push({ id: 'students_stats', label: t('students_stats'), icon: 'bar_chart' });
    }
    if (!isArchived) {
        principalTabs.push({ id: 'student_management', label: t('management'), icon: 'manage_accounts' });
    }

    // Filter kudos for this class
    const classKudos = kudos.filter(k => 
        (k.studentId && classStudents.some(s => s.id === k.studentId)) ||
        (k.teacherId && cls.teacherIds.includes(k.teacherId))
    );

    // Approvals Data
    const pendingEvents = assessmentEvents.filter(e => e.classId === cls.id && e.status === 'SUBMITTED');
    const approvedEvents = assessmentEvents.filter(e => e.classId === cls.id && e.status === 'PUBLISHED');

    if (viewingStudentDM) {
        const student = allStudents.find(s => s.id === viewingStudentDM.id)!;
        const parent = allUsers.find(u => u.id === student.parentId)!;
        const conversationMessages = messages.filter(m => m.classId === cls.id && m.studentId === student.id);
        const isParentDisabled = Boolean(parent?.directChatDisabled || student?.directChatDisabled);
        
        return (
           <div className="flex flex-col h-full bg-white">
            <Header 
              title={`${student.childName} (${parent?.name || 'Parent'})`}
              subtitle={`${cls.name} • Direct Inquiry`}
              user={user} 
              onBack={() => setViewingStudentDM(null)} 
              onLogout={onLogout} 
              onEditProfile={() => setIsEditingProfile(true)}
            />
            
            {/* Principal Moderation Bar */}
            <div className={`px-4 py-2.5 border-b flex items-center justify-between gap-2 text-xs transition-colors ${
                isParentDisabled ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-gray-50 border-gray-200 text-gray-700'
            }`}>
                <div className="flex items-center gap-2">
                    <span className={`material-icons-round text-base ${isParentDisabled ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {isParentDisabled ? 'block' : 'check_circle'}
                    </span>
                    <div>
                        <span className="font-bold">{parent?.name || 'Parent'}</span>
                        <span className="text-gray-500 ml-1">({parent?.email || ''})</span>
                        <p className="text-[11px] text-gray-500">
                            Status: <strong className={isParentDisabled ? 'text-red-600' : 'text-emerald-600'}>{isParentDisabled ? 'Direct Messaging Disabled' : 'Direct Messaging Active'}</strong>
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => {
                        if (parent) {
                            const newStatus = !isParentDisabled;
                            onUpdateUser(parent.id, { directChatDisabled: newStatus });
                        }
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs ${
                        isParentDisabled
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            : 'bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200'
                    }`}
                >
                    <span className="material-icons-round text-sm">{isParentDisabled ? 'lock_open' : 'block'}</span>
                    <span>{isParentDisabled ? 'Enable Direct Chat' : 'Disable Direct Chat'}</span>
                </button>
            </div>

            <div className="flex-1 overflow-hidden">
               <ChatWindow 
                 messages={conversationMessages} 
                 announcements={[]} 
                 onSendMessage={handlePrincipalSendMessage}
                 user={user}
                 targetLanguage={parent.language}
                 readOnly={isArchived}
                 allUsers={allUsers}
                 setViewingImageUrl={setViewingImageUrl}
                 onUpdateMessage={(id, text) => onUpdateMessage(id, text, parent.language)}
                 onDeleteMessage={onDeleteMessage}
                 onEditAnnouncement={setEditingAnnouncement}
                 onDeleteAnnouncement={onDeleteAnnouncement}
                 kudos={classKudos.filter(k => k.studentId === student.id)}
                 students={[student]}
                 isDirectChat={true}
                 allowAttachments={true}
                 onMarkMessagesRead={onMarkMessagesRead}
               />
            </div>
          </div>
        )
    }

    if (printingEvent) {
        const results = assessmentResults.filter(r => r.assessmentId === printingEvent.id);
        const students = classStudents.filter(s => s.approvalStatus === 'approved');
        return (
            <div className="flex flex-col h-full bg-white relative">
                <div className="no-print p-4 border-b flex justify-between items-center bg-indigo-600 text-white">
                    <h2 className="font-bold text-lg">Print Center: {printingEvent.title}</h2>
                    <div className="flex gap-2">
                        <button onClick={() => window.print()} className="bg-white text-indigo-600 px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-gray-100">
                            <span className="material-icons-round">print</span> Print
                        </button>
                        <button onClick={() => setPrintingEvent(null)} className="bg-indigo-700 text-white p-2 rounded-lg hover:bg-indigo-800">
                            <span className="material-icons-round">close</span>
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-auto bg-gray-100 p-8">
                    <BatchPrintView 
                        assessment={printingEvent}
                        students={students}
                        results={results}
                        org={org}
                    />
                </div>
            </div>
        )
    }

    if (reviewingEvent) {
        return (
            <AssessmentGradingView 
                assessment={reviewingEvent}
                students={classStudents.filter(s => s.approvalStatus === 'approved')}
                results={assessmentResults.filter(r => r.assessmentId === reviewingEvent.id)}
                onSaveResult={onSaveAssessmentResult}
                onStatusChange={async (status, note) => {
                    await onSaveAssessmentEvent({ ...reviewingEvent, status, principalNote: note || reviewingEvent.principalNote });
                    setReviewingEvent(null); // Close after status change if published/rejected
                }}
                onClose={() => setReviewingEvent(null)}
                t={t}
                userRole="principal"
            />
        )
    }

    return (
      <>
        <div className="flex flex-col h-full bg-white">
            <Header title={cls.name} subtitle={`${academicYear?.name}`} user={user} onBack={onBack} onLogout={onLogout} onEditProfile={() => setIsEditingProfile(true)} />
            
            <TabMenu items={principalTabs} activeTab={activeTab} onSelectTab={(id) => setActiveTab(id as any)} />

            <div className="flex-1 overflow-y-auto bg-gray-50">
                {activeTab === 'communication' && (
                    <div className="h-full flex flex-col">
                        {/* Two Bucket Switcher */}
                        <div className="bg-white border-b px-4 py-2.5 flex items-center justify-between gap-3 shadow-xs">
                            <div className="flex bg-gray-100 p-1 rounded-xl w-full max-w-md">
                                <button
                                    type="button"
                                    onClick={() => setPrincipalChatMode('group')}
                                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                        principalChatMode === 'group'
                                            ? 'bg-white text-indigo-600 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    <span className="material-icons-round text-base">campaign</span>
                                    <span>Class Broadcasts</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPrincipalChatMode('direct')}
                                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                        principalChatMode === 'direct'
                                            ? 'bg-white text-indigo-600 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    <span className="material-icons-round text-base">forum</span>
                                    <span>Direct Inquiries (1:1)</span>
                                    {totalUnreadDirectCount > 0 && (
                                        <span className="bg-rose-500 text-white text-[10px] px-1.5 py-0.2 rounded-full font-bold animate-pulse">
                                            {totalUnreadDirectCount}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>

                        {principalChatMode === 'group' ? (
                            <div className="flex-1 overflow-hidden flex flex-col">
                                {!isArchived && (
                                    <div className="p-2 border-b bg-white">
                                        <div className="flex items-center justify-between mb-1.5 px-2">
                                            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">{t('student_conversations')}</p>
                                            <button 
                                                onClick={() => setPrincipalChatMode('direct')}
                                                className="text-xs text-indigo-600 font-bold hover:underline flex items-center gap-0.5"
                                            >
                                                <span>View All ({approvedClassStudents.length})</span>
                                                <span className="material-icons-round text-sm">arrow_forward</span>
                                            </button>
                                        </div>
                                        <div className="flex gap-3 overflow-x-auto pb-1.5 scrollbar-hide">
                                            {approvedClassStudents.map(student => {
                                                const hasUnread = messages.some(m => m.studentId === student.id && m.senderId !== user.id && !m.readBy.includes(user.id));
                                                return (
                                                 <button key={student.id} onClick={() => setViewingStudentDM(student)} className="flex flex-col items-center gap-1 w-16 text-center flex-shrink-0 group">
                                                    <Avatar photoUrl={student.photoUrl} name={student.childName} size="md" unread={hasUnread}/>
                                                    <span className="text-[11px] truncate w-full text-gray-700 font-medium group-hover:text-indigo-600">{student.childName}</span>
                                                </button>
                                            )})}
                                        </div>
                                    </div>
                                )}
                                <div className="flex-1 overflow-hidden">
                                    <ChatWindow 
                                        messages={messages.filter(m => m.classId === cls.id && !m.studentId)}
                                        announcements={announcements}
                                        onSendMessage={handlePrincipalSendMessage}
                                        user={user}
                                        targetLanguage={user.language}
                                        readOnly={isArchived}
                                        allUsers={allUsers}
                                        setViewingImageUrl={setViewingImageUrl}
                                        onUpdateMessage={(id, text) => onUpdateMessage(id, text, user.language)}
                                        onDeleteMessage={onDeleteMessage}
                                        onEditAnnouncement={setEditingAnnouncement}
                                        onDeleteAnnouncement={onDeleteAnnouncement}
                                        kudos={classKudos}
                                        students={classStudents}
                                        isDirectChat={false}
                                        onMarkMessagesRead={onMarkMessagesRead}
                                    />
                                </div>
                            </div>
                        ) : (
                            /* Direct Inquiries Management Hub (Principal can manage 40+ parent chats & moderate) */
                            <div className="flex-1 overflow-y-auto bg-gray-50 p-4 space-y-4">
                                {/* Search and Filters */}
                                <div className="bg-white p-3 rounded-2xl shadow-xs border border-gray-100 space-y-3">
                                    <div className="relative">
                                        <span className="material-icons-round absolute left-3 top-2.5 text-gray-400 text-lg">search</span>
                                        <input
                                            type="text"
                                            placeholder="Search student or parent name..."
                                            value={directSearchQuery}
                                            onChange={e => setDirectSearchQuery(e.target.value)}
                                            className="w-full pl-9 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium focus:bg-white focus:border-indigo-500 outline-none"
                                        />
                                        {directSearchQuery && (
                                            <button onClick={() => setDirectSearchQuery('')} className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600">
                                                <span className="material-icons-round text-sm">close</span>
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex gap-2 flex-wrap text-xs">
                                        <button
                                            onClick={() => setDirectFilter('all')}
                                            className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                                                directFilter === 'all'
                                                    ? 'bg-indigo-600 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            All Students ({approvedClassStudents.length})
                                        </button>
                                        <button
                                            onClick={() => setDirectFilter('unread')}
                                            className={`px-3 py-1.5 rounded-lg font-bold transition-all flex items-center gap-1.5 ${
                                                directFilter === 'unread'
                                                    ? 'bg-rose-600 text-white'
                                                    : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                                            }`}
                                        >
                                            <span className="material-icons-round text-xs">mark_chat_unread</span>
                                            <span>Needs Reply / Unread</span>
                                            {totalNeedsAttentionCount > 0 && (
                                                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                                                    directFilter === 'unread' ? 'bg-white text-rose-600' : 'bg-rose-600 text-white'
                                                }`}>
                                                    {totalNeedsAttentionCount}
                                                </span>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => setDirectFilter('active')}
                                            className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                                                directFilter === 'active'
                                                    ? 'bg-indigo-600 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            Active Conversations
                                        </button>
                                    </div>
                                </div>

                                {/* Thread list */}
                                <div className="space-y-2">
                                    {studentConversationList.length === 0 ? (
                                        <div className="text-center py-12 bg-white rounded-2xl border border-gray-100 text-gray-500">
                                            <span className="material-icons-round text-4xl text-gray-300 mb-2">forum</span>
                                            <p className="font-bold text-sm">No conversations found</p>
                                            <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filter</p>
                                        </div>
                                    ) : (
                                        studentConversationList.map(({ student, parent, unreadCount, lastMsg, isWaitingForReply, hasMessages }) => {
                                            const isParentDisabled = Boolean(parent?.directChatDisabled || student?.directChatDisabled);
                                            return (
                                                <div
                                                    key={student.id}
                                                    className="bg-white p-3.5 rounded-2xl border border-gray-100 shadow-xs hover:border-indigo-200 transition-all flex items-center justify-between gap-3"
                                                >
                                                    <button
                                                        onClick={() => setViewingStudentDM(student)}
                                                        className="flex-1 text-left flex items-center gap-3.5 min-w-0 group"
                                                    >
                                                        <div className="relative flex-shrink-0">
                                                            <Avatar photoUrl={student.photoUrl} name={student.childName} size="md" />
                                                            {unreadCount > 0 && (
                                                                <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold border-2 border-white shadow-xs animate-bounce">
                                                                    {unreadCount}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between mb-0.5">
                                                                <div className="flex items-center gap-2 truncate">
                                                                    <span className="font-bold text-sm text-gray-900 group-hover:text-indigo-600 transition-colors truncate">
                                                                        {student.childName}
                                                                    </span>
                                                                    <span className="text-xs text-gray-400 font-medium truncate">
                                                                        • {parent?.name || 'Parent'}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                                                                    {unreadCount > 0 ? (
                                                                        <span className="bg-rose-100 text-rose-700 font-bold px-2 py-0.5 rounded-md text-[10px] border border-rose-200">
                                                                            Unread ({unreadCount})
                                                                        </span>
                                                                    ) : isWaitingForReply ? (
                                                                        <span className="bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded-md text-[10px] border border-amber-200">
                                                                            Needs Reply
                                                                        </span>
                                                                    ) : hasMessages ? (
                                                                        <span className="bg-emerald-50 text-emerald-700 font-medium px-2 py-0.5 rounded-md text-[10px]">
                                                                            Replied
                                                                        </span>
                                                                    ) : null}
                                                                    {lastMsg?.timestamp && (
                                                                        <span className="text-[11px] text-gray-400">
                                                                            {new Date(lastMsg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                                <p className="truncate max-w-[85%]">
                                                                    {lastMsg ? (
                                                                        <span>
                                                                            <span className="font-semibold text-gray-700">{lastMsg.senderName.split(' ')[0]}: </span>
                                                                            {lastMsg.text || 'Photo attachment'}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-gray-400 italic">No direct messages yet</span>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </button>

                                                    {/* Quick Principal Moderation Action */}
                                                    <div className="flex items-center gap-2 flex-shrink-0 border-l pl-3">
                                                        <button
                                                            title={isParentDisabled ? "Direct Messaging is disabled for this parent. Click to enable." : "Click to disable direct messaging for this parent"}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (parent) {
                                                                    onUpdateUser(parent.id, { directChatDisabled: !isParentDisabled });
                                                                }
                                                            }}
                                                            className={`p-2 rounded-xl text-xs font-semibold flex items-center gap-1 transition-all ${
                                                                isParentDisabled 
                                                                    ? 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200' 
                                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                            }`}
                                                        >
                                                            <span className="material-icons-round text-base">
                                                                {isParentDisabled ? 'block' : 'forum'}
                                                            </span>
                                                            <span className="hidden sm:inline">
                                                                {isParentDisabled ? 'Blocked' : 'Active'}
                                                            </span>
                                                        </button>
                                                        <button
                                                            onClick={() => setViewingStudentDM(student)}
                                                            className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                                                        >
                                                            <span className="material-icons-round text-base">chevron_right</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                 {activeTab === 'homework' && (
                    <PrincipalHomeworkView 
                        classStudents={classStudents.filter(s => s.approvalStatus === 'approved')}
                        allHomeworks={allHomeworks.filter(h => h.classId === cls.id)}
                        studentHomeworkStatuses={studentHomeworkStatuses}
                        stuckLogs={stuckLogs}
                        t={t}
                        tutorLogs={tutorLogs}
                    />
                 )}
                 {activeTab === 'approvals' && (
                     <div className="p-4 space-y-6">
                        {/* Pending Approvals */}
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2">{t('pending_approval')}</h3>
                            {pendingEvents.length === 0 ? (
                                <p className="text-gray-500 bg-white p-4 rounded-xl shadow-sm text-center">{t('no_pending_approvals')}</p>
                            ) : (
                                <div className="space-y-3">
                                    {pendingEvents.map(event => (
                                        <div key={event.id} className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl shadow-sm flex justify-between items-center">
                                            <div>
                                                <h4 className="font-bold text-gray-800">{event.title}</h4>
                                                <p className="text-sm text-gray-600">{new Date(event.date).toLocaleDateString()} • {event.subjects.length} Subjects</p>
                                            </div>
                                            <button 
                                                onClick={() => setReviewingEvent(event)}
                                                className="bg-yellow-100 text-yellow-800 font-bold px-4 py-2 rounded-lg hover:bg-yellow-200"
                                            >
                                                Review
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Published Exams */}
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2">{t('exam_results')}</h3>
                            <div className="space-y-3">
                                {approvedEvents.map(event => (
                                    <div key={event.id} className="bg-white p-4 rounded-xl shadow-sm border flex justify-between items-center">
                                        <div>
                                            <h4 className="font-bold text-gray-800">{event.title}</h4>
                                            <p className="text-sm text-gray-600">{new Date(event.date).toLocaleDateString()}</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button 
                                                onClick={() => setReviewingEvent(event)} 
                                                className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg text-sm font-bold hover:bg-gray-200"
                                            >
                                                View
                                            </button>
                                            <button 
                                                onClick={() => setPrintingEvent(event)} 
                                                className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-lg text-sm font-bold hover:bg-indigo-200 flex items-center gap-1"
                                            >
                                                <span className="material-icons-round text-sm">print</span> {t('print')}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                     </div>
                 )}
                 {activeTab === 'students_stats' && academicYear && (
                     <div className="p-4 space-y-4">
                         {isFeatureEnabled(org, 'attendance') && isFeatureEnabled(org, 'weeklyCheckin') && (
                             <div className="flex gap-2 border-b pb-3">
                                 <button
                                     type="button"
                                     onClick={() => setStatsSubTab('attendance')}
                                     className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                                         statsSubTab === 'attendance'
                                             ? 'bg-indigo-600 text-white shadow-xs'
                                             : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                     }`}
                                 >
                                     <span className="material-icons-round text-sm">how_to_reg</span>
                                     Attendance Register &amp; Ledger
                                 </button>
                                 <button
                                     type="button"
                                     onClick={() => setStatsSubTab('weekly_checkin')}
                                     className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                                         statsSubTab === 'weekly_checkin'
                                             ? 'bg-indigo-600 text-white shadow-xs'
                                             : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                     }`}
                                 >
                                     <span className="material-icons-round text-sm">insights</span>
                                     Weekly Pulse Check-ins
                                 </button>
                             </div>
                         )}
                         {statsSubTab === 'attendance' && isFeatureEnabled(org, 'attendance') ? (
                             <ClassAttendanceRegisterView
                                 cls={cls}
                                 academicYear={academicYear}
                                 students={classStudents}
                                 attendanceRecords={attendanceRecords}
                                 calendarEvents={calendarEvents}
                                 t={t}
                                 isPrincipalView={true}
                             />
                         ) : (
                             <StatsView students={classStudents} logs={weeklyLogs} academicYearStartDate={new Date(academicYear.startDate)} t={t} />
                         )}
                     </div>
                 )}
                {activeTab === 'student_management' && !isArchived && (
                    <StudentManagementView 
                        students={classStudents}
                        orgClasses={destinationClasses}
                        onUpdateStudentStatus={onUpdateStudentStatus}
                        onMoveStudents={onMoveStudents}
                        onInviteParents={() => setShowInviteModal(true)}
                        t={t}
                    />
                )}
            </div>
        </div>
        {isEditingProfile && (
            <EditProfileModal
              user={user}
              onClose={() => setIsEditingProfile(false)}
              onSave={onUpdateUser}
              t={t}
              onDeleteAccount={onDeleteAccount}
            />
        )}
        {showInviteModal && (
            <InviteParentsModal
                className={cls.name}
                orgName={org.name}
                org={org}
                classId={cls.id}
                invitationCodes={invitationCodes.filter(c => c.classId === cls.id && c.type === 'parent')}
                onClose={() => setShowInviteModal(false)}
                onGenerate={(count) => onGenerateInvitationCodes('parent', count, org.id, cls.id)}
                t={t}
                allUsers={allUsers}
                allStudents={allStudents}
                currentUser={user}
            />
        )}
        {editingAnnouncement && academicYear && (
            <BroadcastModal
                onClose={() => setEditingAnnouncement(null)}
                classes={destinationClasses}
                onSend={async () => {}} // Not used in edit mode
                t={t}
                announcementToEdit={editingAnnouncement}
                onUpdate={async (id, data) => onUpdateAnnouncement(id, data)}
            />
        )}
      </>
    );
};

type PrincipalManagerProps = {
  user: UserProfile;
  org: Organization;
  allAcademicYears: AcademicYear[];
  activeYear: AcademicYear | undefined;
  allUsers: UserProfile[];
  classes: ClassRoom[];
  students: StudentConnection[];
  allMessages: Message[];
  announcements: Announcement[];
  weeklyLogs: WeeklyLog[];
  allHomeworks: Homework[];
  studentHomeworkStatuses: StudentHomeworkStatus[];
  stuckLogs: StuckLog[];
  attendanceRecords: AttendanceRecord[];
  kudos: Kudo[];
  onLogout: () => void;
  onCreateClass: (name: string, academicYearId: string) => void;
  onUpdateClass: (classId: string, data: Partial<ClassRoom>) => void;
  onDeleteClass: (classId: string) => void;
  onSendAnnouncement: (title: string, text: string, imageUrl: string | undefined, targetClassIds: string[], academicYearId: string) => Promise<void>;
  onAssignTeachers: (classId: string, teacherIds: string[], classTeacherId?: string) => void;
  onUpdateUser: (userId: string, data: Partial<UserProfile>) => void;
  onUpdateOrg: (orgId: string, data: Partial<Organization>) => void;
  onSendMessage: (message: Omit<Message, 'id' | 'timestamp' | 'readBy'>) => void;
  onUpdateMessage: (messageId: string, newText: string, targetLanguage: string) => void;
  onDeleteMessage: (messageId: string) => void;
  onUpdateAnnouncement: (announcementId: string, data: Partial<Omit<Announcement, 'id' | 'timestamp'>>) => void;
  onDeleteAnnouncement: (announcementId: string) => void;
  setViewingImageUrl: (url: string | null) => void; 
  onCreateClasses: (newClasses: Omit<ClassRoom, 'id' | 'teacherIds' | 'academicYearId'>[], academicYearId: string) => Promise<ClassRoom[]>;
  onUpdateStudentStatus: (studentIds: string[], status: 'removed' | 'graduated') => void;
  onMoveStudents: (studentIds: string[], newClassId: string, newAcademicYearId: string) => void; 
  onCreateOrUpdateAcademicYear: (year: Omit<AcademicYear, 'id'> | AcademicYear) => Promise<AcademicYear>;
  onDeleteAcademicYear: (yearId: string) => void;
  onActivateYear: (yearId: string) => void; 
  onGenerateInvitationCodes: (type: InvitationType, count: number, orgId: string, classId?: string) => Promise<InvitationCode[]>;
  onCreatePendingTeacher: (name: string, email: string, orgId: string) => void;
  calendarEvents: CalendarEvent[];
  onCreateOrUpdateCalendarEvent: (event: Omit<CalendarEvent, 'id'> | CalendarEvent) => Promise<void>;
  onDeleteCalendarEvent: (eventId: string) => Promise<void>;
  onSendKudo: (kudo: Omit<Kudo, 'id' | 'timestamp'>) => Promise<void>;
  tutorLogs: TutorLog[];
  onDeleteAccount: () => void;
  assessmentEvents: AssessmentEvent[];
  assessmentResults: AssessmentResult[];
  onSaveAssessmentEvent: (e: any) => Promise<void>;
  onSaveAssessmentResult: (r: any) => Promise<void>;
  invitationCodes?: InvitationCode[];
  onMarkMessagesRead?: (messageIds: string[]) => void;
  onSwitchToTeacherMode?: () => void;
  onTransferOwnership?: (params: {
    orgId: string;
    newAdminEmail: string;
    newAdminId?: string;
    newAdminName?: string;
    outgoingAdminRole: 'vice_principal' | 'teacher' | 'stepped_down';
    reason?: string;
    referenceTicket?: string;
    transferType?: 'self_serve' | 'super_admin_reassignment';
  }) => Promise<void>;
};

export const PrincipalManager = ({
  user, org, allAcademicYears, activeYear, allUsers, classes, students, allMessages, announcements, weeklyLogs, allHomeworks, studentHomeworkStatuses, stuckLogs, attendanceRecords, kudos, onLogout, onCreateClass, onUpdateClass, onDeleteClass, onSendAnnouncement, onAssignTeachers, onUpdateUser, onUpdateOrg, onSendMessage, onUpdateMessage, onDeleteMessage, onUpdateAnnouncement, onDeleteAnnouncement, setViewingImageUrl, onCreateClasses, onUpdateStudentStatus, onMoveStudents, onCreateOrUpdateAcademicYear, onDeleteAcademicYear, onActivateYear, onGenerateInvitationCodes, onCreatePendingTeacher, calendarEvents, onCreateOrUpdateCalendarEvent, onDeleteCalendarEvent, onSendKudo, tutorLogs, onDeleteAccount, assessmentEvents, assessmentResults, onSaveAssessmentEvent, onSaveAssessmentResult, invitationCodes = [], onMarkMessagesRead, onSwitchToTeacherMode, onTransferOwnership
}: PrincipalManagerProps) => {
  const [viewingClassId, setViewingClassId] = useState<string | null>(null);
  const [mainView, setMainView] = useState<'dashboard' | 'kudosLog'>('dashboard');
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [assigningToClass, setAssigningToClass] = useState<ClassRoom | null>(null);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showStaffChat, setShowStaffChat] = useState(false);
  const [editingClass, setEditingClass] = useState<ClassRoom | null>(null);
  const [expandedYear, setExpandedYear] = useState<string | null>(activeYear?.id || null);
  const [showManageYear, setShowManageYear] = useState(false);
  const [editingYear, setEditingYear] = useState<AcademicYear | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [isCalendarViewOpen, setIsCalendarViewOpen] = useState(false);
  const [isKudoModalOpen, setIsKudoModalOpen] = useState(false);
  const [isTeacherKudoModalOpen, setIsTeacherKudoModalOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [showModulesModal, setShowModulesModal] = useState(false);
  const [showQAModal, setShowQAModal] = useState(false);
  const [showVicePrincipalsModal, setShowVicePrincipalsModal] = useState(false);
  const [showTransferOwnershipModal, setShowTransferOwnershipModal] = useState(false);
  const [deletingYearId, setDeletingYearId] = useState<string | null>(null);

  const handleDeleteAcademicYear = async (year: AcademicYear) => {
    const yearClasses = classes.filter(c => c.academicYearId === year.id);
    const enrolledStudents = students.filter(s => s.academicYearId === year.id || yearClasses.some(c => c.id === s.classId));

    if (enrolledStudents.length > 0) {
      alert(`Cannot delete "${year.name}" because it has ${enrolledStudents.length} student(s) enrolled. Please move or remove the students before deleting this session.`);
      return;
    }

    let confirmMsg = `Are you sure you want to delete the academic year "${year.name}"?`;
    if (yearClasses.length > 0) {
      confirmMsg += `\n\nThis will also remove ${yearClasses.length} class(es) configured for this session.`;
    }

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      setDeletingYearId(year.id);
      await onDeleteAcademicYear(year.id);
    } catch (err: any) {
      console.error("Failed to delete academic year:", err);
      alert(`Could not delete "${year.name}": ${err?.message || 'Permission denied or network connection issue.'}`);
    } finally {
      setDeletingYearId(null);
    }
  };

  const isPrimaryPrincipal = user.id === org.adminId;
  
  const t = useTranslate(user.language);
  const pendingTeachers = allUsers.filter(u => u.role === 'teacher' && u.orgId === org.id && u.approvalStatus === 'pending');
  const activeTeachers = allUsers.filter(u => u.role === 'teacher' && u.orgId === org.id && u.approvalStatus === 'approved');

  const myAssignedClasses = React.useMemo(() => {
    return classes.filter(c => c.orgId === org.id && c.teacherIds && c.teacherIds.includes(user.id));
  }, [classes, org.id, user.id]);

  const assignableTeachers = React.useMemo(() => {
    const list = allUsers.filter(u => 
      (u.role === 'teacher' || u.role === 'principal' || u.isVicePrincipal || u.id === org.adminId || (org.vicePrincipalEmails || []).includes(u.email)) &&
      (u.orgId === org.id || u.id === org.adminId || (org.vicePrincipalEmails || []).includes(u.email)) &&
      (u.approvalStatus === 'approved' || u.role === 'principal' || u.id === org.adminId || u.isVicePrincipal)
    );

    return list.sort((a, b) => {
      const aIsAdmin = a.id === org.adminId;
      const bIsAdmin = b.id === org.adminId;
      if (aIsAdmin && !bIsAdmin) return -1;
      if (!aIsAdmin && bIsAdmin) return 1;

      const aIsVP = a.isVicePrincipal || (org.vicePrincipalEmails || []).includes(a.email);
      const bIsVP = b.isVicePrincipal || (org.vicePrincipalEmails || []).includes(b.email);
      if (aIsVP && !bIsVP) return -1;
      if (!aIsVP && bIsVP) return 1;

      return (a.name || '').localeCompare(b.name || '');
    });
  }, [allUsers, org]);
  
  const staffChatId = `STAFF_CHAT_${org.id}`;

  const now = new Date();
  const readyToActivateYear = allAcademicYears.find(y => !y.isActivated && new Date(y.startDate) <= now && new Date(y.endDate) >= now);

  const atAGlanceStats = React.useMemo(() => {
    if (!activeYear) return null;
    
    const studentsInActiveYear = students.filter(s => s.academicYearId === activeYear.id && s.approvalStatus === 'approved');
    const studentIdsInActiveYear = new Set(studentsInActiveYear.map(s => s.id));
    
    // Today's Attendance
    const todayStr = new Date().toISOString().split('T')[0];
    const todaysRecords = attendanceRecords.filter(r => r.date === todayStr && studentIdsInActiveYear.has(r.studentId));
    const presentCount = todaysRecords.filter(r => r.status === 'present' || r.status === 'late').length;
    const attendancePercentage = todaysRecords.length > 0 ? (presentCount / todaysRecords.length) * 100 : 100;

    // Weekly Check-in Rate
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff)).toISOString().split('T')[0];
    const logsThisWeek = weeklyLogs.filter(l => l.date === monday && studentIdsInActiveYear.has(l.studentId));
    const checkinRate = studentsInActiveYear.length > 0 ? (logsThisWeek.length / studentsInActiveYear.length) * 100 : 0;

    return {
        activeStudents: studentsInActiveYear.length,
        attendance: attendancePercentage,
        checkinRate: checkinRate,
        pendingTeachers: pendingTeachers.length,
    }
  }, [activeYear, students, attendanceRecords, weeklyLogs, pendingTeachers]);

  const handleGenerateTeacherCodes = (count: number) => {
    return onGenerateInvitationCodes('teacher', count, org.id);
  };

  const handleCreatePendingTeacher = (name: string, email: string) => {
    onCreatePendingTeacher(name, email, org.id);
  };
  
  // Filter teacher kudos to display in staff chat
  const teacherKudos = kudos.filter(k => k.teacherId && activeTeachers.some(t => t.id === k.teacherId));

  if (showStaffChat && activeYear) {
    return (
        <div className="flex flex-col h-full bg-white">
            <Header 
                title={`${org.name} Staff Room`} 
                subtitle="Staff Chat"
                user={user}
                onBack={() => setShowStaffChat(false)}
                onLogout={onLogout}
                onEditProfile={() => setIsEditingProfile(true)}
            />
            <div className="flex-1 overflow-hidden">
                <ChatWindow 
                    messages={allMessages.filter(m => m.classId === staffChatId && m.academicYearId === activeYear.id)}
                    announcements={announcements.filter(a => a.classIds.includes(staffChatId))} // Ensure announcements targeting staff chat appear
                    kudos={teacherKudos}
                    onSendMessage={(text, original, img) => onSendMessage({
                        classId: staffChatId,
                        senderId: user.id,
                        senderName: user.name,
                        senderRole: user.role,
                        text, originalText: original, originalLanguage: user.language, imageUrl: img,
                        academicYearId: activeYear.id
                    })}
                    user={user}
                    targetLanguage={user.language}
                    allUsers={allUsers}
                    setViewingImageUrl={setViewingImageUrl}
                    onUpdateMessage={onUpdateMessage}
                    onDeleteMessage={onDeleteMessage}
                    students={[]}
                />
            </div>
        </div>
    );
  }

  if (mainView === 'kudosLog' && activeYear) {
    return (
        <PrincipalKudosLog
            onBack={() => setMainView('dashboard')}
            kudos={kudos.filter(k => k.academicYearId === activeYear.id)}
            students={students.filter(s => s.academicYearId === activeYear.id)}
            classes={classes.filter(c => c.academicYearId === activeYear.id)}
            teachers={activeTeachers}
            user={user}
            t={t}
        />
    )
  }

  if (viewingClassId) {
    const cls = classes.find(c => c.id === viewingClassId)!;
    const academicYear = allAcademicYears.find(y => y.id === cls.academicYearId);
    const classAnnouncements = announcements.filter(a => a.academicYearId === cls.academicYearId && (a.classIds.length === 0 || a.classIds.includes(cls.id)));
    
    const destinationClasses = classes.filter(c => {
        if (c.id === viewingClassId) return false;

        const yearOfClass = allAcademicYears.find(y => y.id === c.academicYearId);
        if (!yearOfClass) return false;

        const isArchived = activeYear?.id !== yearOfClass.id && new Date(yearOfClass.endDate) < now;
        return !isArchived;
    });

    return (
      <PrincipalClassView 
        user={user}
        cls={cls}
        org={org}
        allStudents={students}
        messages={allMessages.filter(m => m.academicYearId === cls.academicYearId)}
        announcements={classAnnouncements}
        weeklyLogs={weeklyLogs.filter(d => d.academicYearId === cls.academicYearId)}
        allHomeworks={allHomeworks}
        studentHomeworkStatuses={studentHomeworkStatuses}
        stuckLogs={stuckLogs}
        allUsers={allUsers}
        onBack={() => setViewingClassId(null)}
        setViewingImageUrl={setViewingImageUrl}
        onLogout={onLogout}
        onUpdateUser={onUpdateUser}
        onUpdateStudentStatus={onUpdateStudentStatus}
        onMoveStudents={onMoveStudents}
        onSendMessage={onSendMessage}
        onUpdateMessage={onUpdateMessage}
        onDeleteMessage={onDeleteMessage}
        onUpdateAnnouncement={onUpdateAnnouncement}
        onDeleteAnnouncement={onDeleteAnnouncement}
        isArchived={!academicYear?.isActivated || (activeYear?.id !== academicYear?.id)}
        academicYear={academicYear}
        destinationClasses={destinationClasses}
        t={t}
        tutorLogs={tutorLogs}
        onGenerateInvitationCodes={onGenerateInvitationCodes}
        kudos={kudos}
        onDeleteAccount={onDeleteAccount}
        assessmentEvents={assessmentEvents}
        assessmentResults={assessmentResults}
        onSaveAssessmentEvent={onSaveAssessmentEvent}
        onSaveAssessmentResult={onSaveAssessmentResult}
        invitationCodes={invitationCodes}
        onMarkMessagesRead={onMarkMessagesRead}
        attendanceRecords={attendanceRecords}
        calendarEvents={calendarEvents}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col h-full bg-gray-50">
        <Header 
          user={user} 
          title={org.name} 
          subtitle={<>{isPrimaryPrincipal ? t('principal') : 'Vice Principal (Co-Admin)'} ({activeYear?.name || '...'})</>} 
          onLogout={onLogout} 
          onEditProfile={() => setIsEditingProfile(true)}
          orgLogoUrl={org.photoUrl}
          onShowHelp={() => setShowHelp(true)}
          onShowPolicy={() => setShowPolicy(true)}
        >
            {onSwitchToTeacherMode && (
                <button 
                    onClick={onSwitchToTeacherMode}
                    className="relative flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-full cursor-pointer transition-all text-xs font-bold shadow-2xs"
                    title="Switch to My Classes (Teacher Workspace)"
                >
                    <span className="material-icons-round text-base text-indigo-600">school</span>
                    <span className="hidden sm:inline">My Classes</span>
                    {myAssignedClasses.length > 0 && (
                        <span className="min-w-[17px] h-4 px-1 bg-indigo-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                            {myAssignedClasses.length}
                        </span>
                    )}
                </button>
            )}
            <button 
                onClick={() => setShowVicePrincipalsModal(true)}
                className="relative p-2 text-gray-400 hover:bg-gray-100 rounded-full cursor-pointer"
                title="Vice Principals & Co-Admins"
            >
                <span className="material-icons-round">supervisor_account</span>
                {(org.vicePrincipalEmails?.length || 0) > 0 && (
                    <span className="absolute top-0 right-0 min-w-[15px] h-3.5 px-0.5 bg-teal-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center border border-white">
                        {org.vicePrincipalEmails?.length}
                    </span>
                )}
            </button>
            <button 
                onClick={() => setShowStaffModal(true)}
                className="relative p-2 text-gray-400 hover:bg-gray-100 rounded-full cursor-pointer"
            >
                <span className="material-icons-round">groups</span>
                {pendingTeachers.length > 0 && (
                    <span className="absolute top-0 right-0 h-2.5 w-2.5 bg-red-500 rounded-full border-2 border-white"></span>
                )}
            </button>
        </Header>

        <WelcomeBanner user={user} org={org} onEditOrg={() => setIsEditingOrg(true)}>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border shadow-xs ${isPrimaryPrincipal ? 'bg-amber-100 text-amber-900 border-amber-200' : 'bg-teal-100 text-teal-900 border-teal-200'}`}>
                {isPrimaryPrincipal ? 'Primary Principal' : 'Vice Principal (Co-Admin)'}
            </span>
        </WelcomeBanner>
        
        {readyToActivateYear && (
          <div className="p-4 bg-green-100 text-green-800 text-center">
            <p className="font-bold">The '{readyToActivateYear.name}' academic year is ready to begin.</p>
            <button
              onClick={() => {
                if(window.confirm(`Are you sure you want to activate ${readyToActivateYear.name}? This will archive the previous year.`)) {
                    onActivateYear(readyToActivateYear.id);
                    if(window.confirm("Do you want to send a notification to all parents and teachers about the new academic year?")) {
                       onSendAnnouncement(`Welcome to the ${readyToActivateYear.name} academic year! Your app has been updated with new class information.`, "Welcome to the new academic year!", undefined, [], readyToActivateYear.id);
                    }
                }
              }} 
              className="mt-2 px-4 py-2 bg-green-600 text-white font-bold rounded-lg shadow"
            >
              Activate Now
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 z-0">
            {allAcademicYears.length === 0 && (
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-indigo-100 text-center mb-6">
                    <div className="bg-indigo-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce-slow">
                        <span className="material-icons-round text-4xl text-indigo-500">school</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Welcome to {org.name}!</h3>
                    <p className="text-gray-500 mb-6 max-w-md mx-auto">To get started, create your first Academic Year. This acts as the container for all your classes and students.</p>
                    <button
                        onClick={() => { setEditingYear(null); setShowManageYear(true); }}
                        className="px-8 py-4 bg-indigo-600 text-white font-bold rounded-xl shadow-lg hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 mx-auto"
                    >
                        <span className="material-icons-round">add_circle</span>
                        Create Academic Year
                    </button>
                </div>
            )}

            {atAGlanceStats && allAcademicYears.length > 0 && <AtAGlance stats={atAGlanceStats} t={t} />}
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                 {/* Communication: Staff Chat */}
                 {isFeatureEnabled(org, 'chat') && (
                   <button 
                    onClick={() => setShowStaffChat(true)}
                    className="w-full bg-slate-700 text-white p-3.5 rounded-xl font-bold flex flex-col items-center justify-center gap-1.5 shadow-md hover:bg-slate-800 transition-colors cursor-pointer"
                  >
                    <span className="material-icons-round text-2xl">forum</span>
                    <span className="text-xs">{t('staff_chat')}</span>
                  </button>
                 )}

                 {/* Broadcasts */}
                 {isFeatureEnabled(org, 'announcements') && (
                  <button 
                    onClick={() => { if(activeYear) setShowBroadcastModal(true) }}
                    disabled={!activeYear}
                    className="w-full bg-orange-500 text-white p-3.5 rounded-xl font-bold flex flex-col items-center justify-center gap-1.5 shadow-md hover:bg-orange-600 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <span className="material-icons-round text-2xl">campaign</span>
                    <span className="text-xs">{t('broadcast')}</span>
                  </button>
                 )}

                 {/* Calendar */}
                 {isFeatureEnabled(org, 'calendar') && (
                  <button 
                    onClick={() => { if(activeYear) setIsCalendarViewOpen(true) }}
                    disabled={!activeYear}
                    className="w-full bg-blue-500 text-white p-3.5 rounded-xl font-bold flex flex-col items-center justify-center gap-1.5 shadow-md hover:bg-blue-600 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <span className="material-icons-round text-2xl">event</span>
                    <span className="text-xs">{t('smart_calendar')}</span>
                  </button>
                 )}

                 {/* Kudos & Culture */}
                 {isFeatureEnabled(org, 'kudos') && (
                  <>
                    <button 
                      onClick={() => { if(activeYear) setMainView('kudosLog') }}
                      disabled={!activeYear}
                      className="w-full bg-pink-500 text-white p-3.5 rounded-xl font-bold flex flex-col items-center justify-center gap-1.5 shadow-md hover:bg-pink-600 transition-colors cursor-pointer disabled:opacity-50"
                    >
                      <span className="material-icons-round text-2xl">receipt_long</span>
                      <span className="text-xs">{t('kudos_log')}</span>
                    </button>
                    <button 
                      onClick={() => { if(activeYear) setIsKudoModalOpen(true) }}
                      disabled={!activeYear}
                      className="w-full bg-amber-400 text-amber-950 p-3.5 rounded-xl font-bold flex flex-col items-center justify-center gap-1.5 shadow-md hover:bg-amber-500 transition-colors cursor-pointer disabled:opacity-50"
                    >
                      <span className="material-icons-round text-2xl">military_tech</span>
                      <span className="text-xs">{t('send_kudo')}</span>
                    </button>
                    <button 
                      onClick={() => { if(activeYear) setIsTeacherKudoModalOpen(true) }}
                      disabled={!activeYear}
                      className="w-full bg-purple-600 text-white p-3.5 rounded-xl font-bold flex flex-col items-center justify-center gap-1.5 shadow-md hover:bg-purple-700 transition-colors cursor-pointer disabled:opacity-50"
                    >
                      <span className="material-icons-round text-2xl">volunteer_activism</span>
                      <span className="text-xs">Appreciate Teacher</span>
                    </button>
                  </>
                 )}

                {/* Vice Principals / Co-Admins Delegation */}
                <button 
                  onClick={() => setShowVicePrincipalsModal(true)}
                  className="w-full bg-teal-700 text-white p-3.5 rounded-xl font-bold flex flex-col items-center justify-center gap-1.5 shadow-md hover:bg-teal-800 transition-colors cursor-pointer"
                  title="Manage Vice Principals and Co-Admins"
                >
                  <div className="relative">
                    <span className="material-icons-round text-2xl">supervisor_account</span>
                    {(org.vicePrincipalEmails?.length || 0) > 0 && (
                      <span className="absolute -top-1 -right-2.5 bg-amber-400 text-teal-950 text-[10px] font-black px-1.5 py-0.2 rounded-full border border-white">
                        {org.vicePrincipalEmails?.length}
                      </span>
                    )}
                  </div>
                  <span className="text-xs">Vice Principals</span>
                </button>

                {/* Features & Policies Manager */}
                <button 
                  onClick={() => setShowModulesModal(true)}
                  className="w-full bg-indigo-600 text-white p-3.5 rounded-xl font-bold flex flex-col items-center justify-center gap-1.5 shadow-md hover:bg-indigo-700 transition-colors cursor-pointer"
                  title="Configure parent experience policies and custom AI key"
                >
                  <span className="material-icons-round text-2xl">tune</span>
                  <span className="text-xs">Features & Policies</span>
                </button>

                {/* System QA & Diagnostics */}
                <button 
                  onClick={() => setShowQAModal(true)}
                  className="w-full bg-emerald-700 text-white p-3.5 rounded-xl font-bold flex flex-col items-center justify-center gap-1.5 shadow-md hover:bg-emerald-800 transition-colors cursor-pointer"
                  title="Run Automated QA Health Checks across all roles & policies"
                >
                  <span className="material-icons-round text-2xl">health_and_safety</span>
                  <span className="text-xs">System QA</span>
                </button>

                {/* Teaching Workspace / My Classes Switcher */}
                {onSwitchToTeacherMode && (
                  <button 
                    onClick={onSwitchToTeacherMode}
                    className="w-full bg-slate-800 text-white p-3.5 rounded-xl font-bold flex flex-col items-center justify-center gap-1.5 shadow-md hover:bg-slate-900 transition-colors cursor-pointer relative"
                    title="Open your assigned classes in Teacher Workspace"
                  >
                    <div className="relative">
                      <span className="material-icons-round text-2xl text-indigo-300">school</span>
                      {myAssignedClasses.length > 0 && (
                        <span className="absolute -top-1 -right-2.5 bg-emerald-400 text-slate-950 text-[10px] font-black px-1.5 py-0.2 rounded-full border border-white">
                          {myAssignedClasses.length}
                        </span>
                      )}
                    </div>
                    <span className="text-xs">My Classes (Teaching)</span>
                  </button>
                )}
              </div>

              {/* Teaching Load Banner for Principal / Vice Principal */}
              {myAssignedClasses.length > 0 && onSwitchToTeacherMode && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-3.5 mb-2 flex items-center justify-between gap-3 shadow-xs">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center flex-shrink-0 shadow-xs">
                      <span className="material-icons-round text-xl">school</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-gray-900 flex items-center gap-2">
                        Active Teaching Assignment
                        <span className="bg-indigo-100 text-indigo-700 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                          {myAssignedClasses.length} {myAssignedClasses.length === 1 ? 'class' : 'classes'}
                        </span>
                      </p>
                      <p className="text-[11px] text-gray-500 truncate mt-0.5">
                        {myAssignedClasses.map(c => c.name).join(', ')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onSwitchToTeacherMode}
                    className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 flex-shrink-0 cursor-pointer"
                  >
                    Open Teaching View
                    <span className="material-icons-round text-sm">arrow_forward</span>
                  </button>
                </div>
              )}

              <div className="space-y-2 pt-4">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xs font-bold text-gray-500 uppercase">{t('class_management')}</h3>
                    <button
                        onClick={() => { setEditingYear(null); setShowManageYear(true); }}
                        className="px-3 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1"
                    >
                        <span className="material-icons-round text-sm">add</span> {t('create_academic_year')}
                    </button>
                </div>
                {allAcademicYears.sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()).map(year => {
                  const yearClasses = classes.filter(c => c.academicYearId === year.id);
                  const isExpanded = expandedYear === year.id;
                  const yearStatus = activeYear?.id === year.id ? 'ACTIVE' : (new Date(year.endDate) < now ? 'ARCHIVED' : 'UPCOMING');
                  
                  return (
                    <div key={year.id} className="bg-white rounded-xl shadow-sm border border-gray-100">
                      <div className="w-full p-4 text-left flex justify-between items-center">
                        <button onClick={() => setExpandedYear(isExpanded ? null : year.id)} className="flex-1 flex items-center gap-2">
                          <h4 className="font-bold text-gray-800">{year.name}</h4>
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${yearStatus === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {t(`status_${yearStatus.toLowerCase()}`)}
                          </span>
                        </button>
                        <div className="flex items-center">
                            {yearStatus !== 'ARCHIVED' && (
                                <button
                                    onClick={() => { setEditingYear(year); setShowManageYear(true); }}
                                    className="p-2 text-gray-400 hover:bg-gray-100 rounded-full"
                                    title={`Settings for '${year.name}'`}
                                >
                                    <span className="material-icons-round text-base">settings</span>
                                </button>
                            )}
                            {yearStatus === 'UPCOMING' && (
                                <button
                                    type="button"
                                    disabled={deletingYearId === year.id}
                                    onClick={() => handleDeleteAcademicYear(year)}
                                    className="p-2 text-gray-400 hover:bg-red-100 hover:text-red-600 rounded-full disabled:opacity-50 transition-colors"
                                    title={`Delete '${year.name}'`}
                                >
                                    {deletingYearId === year.id ? (
                                        <span className="material-icons-round text-base animate-spin text-red-600">sync</span>
                                    ) : (
                                        <span className="material-icons-round text-base">delete</span>
                                    )}
                                </button>
                            )}
                            <button onClick={() => setExpandedYear(isExpanded ? null : year.id)} className="p-2 text-gray-500">
                                <span className={`material-icons-round transition-transform ${isExpanded ? 'rotate-180' : ''}`}>expand_more</span>
                            </button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="p-4 border-t border-gray-100 space-y-2">
                          {yearClasses.map(cls => {
                            const teachers = allUsers.filter(u => cls.teacherIds.includes(u.id));
                            const classTeacher = allUsers.find(u => u.id === cls.classTeacherId);
                            const classStudents = students.filter(s => s.classId === cls.id && s.approvalStatus === 'approved');
                            const todayStr = new Date().toISOString().split('T')[0];
                            const todayClassRecords = attendanceRecords.filter(r => r.classId === cls.id && r.date === todayStr);
                            const todayPresent = todayClassRecords.filter(r => r.status === 'present' || r.status === 'late').length;
                            const isAttendanceTaken = todayClassRecords.length > 0;
                            const todayPct = isAttendanceTaken && classStudents.length > 0 ? Math.round((todayPresent / classStudents.length) * 100) : null;
                            return (
                              <div key={cls.id} className="w-full bg-gray-50 p-3 rounded-lg text-left">
                                <div className="flex justify-between items-start">
                                   <button onClick={() => setViewingClassId(cls.id)} className="flex-1 text-left group">
                                      <div className="flex items-center gap-2 flex-wrap mb-1">
                                          <h4 className="font-medium text-gray-800 group-hover:text-indigo-600 transition-colors">{cls.name}</h4>
                                          <span className="text-[11px] font-semibold text-gray-500 bg-gray-200/80 px-2 py-0.5 rounded-full">
                                            {classStudents.length} Students
                                          </span>
                                          {isFeatureEnabled(org, 'attendance') && (
                                            isAttendanceTaken ? (
                                              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                                                (todayPct ?? 100) >= 75 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                              }`}>
                                                <span className="material-icons-round !text-[12px]">check_circle</span>
                                                Today: {todayPresent}/{classStudents.length} ({todayPct}%)
                                              </span>
                                            ) : (
                                              <span className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                                                <span className="material-icons-round !text-[12px]">schedule</span>
                                                Attendance Pending
                                              </span>
                                            )
                                          )}
                                      </div>
                                      <p className="text-xs text-gray-500">
                                        {t('teachers')}{' '}
                                        {teachers.length > 0 ? teachers.map((t, i) => (
                                            <span key={t.id}>
                                                {t.name}
                                                {t.id === classTeacher?.id && ' ★'}
                                                {i < teachers.length - 1 ? ', ' : ''}
                                            </span>
                                        )) : t('none_assigned')}
                                      </p>
                                   </button>
                                   {yearStatus !== 'ARCHIVED' && (
                                     <div className="flex gap-2">
                                      <button onClick={() => setEditingClass(cls)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full"><span className="material-icons-round text-sm">edit</span></button>
                                       <button onClick={() => onDeleteClass(cls.id)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full"><span className="material-icons-round text-sm">delete</span></button>
                                       <button 
                                          onClick={() => setAssigningToClass(cls)}
                                          className="ml-2 px-3 py-2 bg-slate-100 text-slate-600 text-xs font-bold rounded-lg hover:bg-slate-200 transition-colors"
                                        >
                                          {t('assign_teachers')}
                                        </button>
                                     </div>
                                   )}
                                </div>
                              </div>
                            );
                          })}
                          {yearStatus !== 'ARCHIVED' && (
                              <button 
                                  onClick={() => setEditingClass({ academicYearId: year.id } as ClassRoom)}
                                  className="w-full mt-2 p-2 bg-indigo-600 text-white font-bold rounded-lg text-sm flex items-center justify-center gap-1 hover:bg-indigo-700"
                              >
                                  <span className="material-icons-round text-base">add</span> {t('add_new_class')}
                              </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
        </div>
      </div>
      {showHelp && (
        <HelpModal role="principal" onClose={() => setShowHelp(false)} />
      )}
      {showPolicy && (
        <PolicyModal onClose={() => setShowPolicy(false)} />
      )}
      {isKudoModalOpen && activeYear && (
        <SendKudoModal
            isOpen={isKudoModalOpen}
            onClose={() => setIsKudoModalOpen(false)}
            user={user}
            students={students.filter(s => s.academicYearId === activeYear.id && s.approvalStatus === 'approved')}
            activeYear={activeYear}
            onSendKudo={onSendKudo}
            t={t}
        />
      )}
      {isTeacherKudoModalOpen && activeYear && (
        <SendPrincipalToTeacherKudoModal
            isOpen={isTeacherKudoModalOpen}
            onClose={() => setIsTeacherKudoModalOpen(false)}
            user={user}
            teachers={activeTeachers}
            activeYear={activeYear}
            onSendKudo={onSendKudo}
            t={t}
        />
      )}
      {isEditingProfile && (
          <EditProfileModal
            user={user}
            onClose={() => setIsEditingProfile(false)}
            onSave={onUpdateUser}
            t={t}
            onDeleteAccount={onDeleteAccount}
          />
      )}
      {editingClass && <CreateEditClassModal 
          onClose={() => setEditingClass(null)} 
          onSave={(name) => {
              if (editingClass.id) {
                  onUpdateClass(editingClass.id, { name });
              } else {
                  onCreateClass(name, editingClass.academicYearId);
              }
          }} 
          cls={editingClass} 
          t={t}
      />}
      {showBroadcastModal && activeYear && <BroadcastModal onClose={() => setShowBroadcastModal(false)} classes={classes.filter(c => c.academicYearId === activeYear.id)} onSend={async (title, text, imageUrl, classIds) => onSendAnnouncement(title, text, imageUrl, classIds, activeYear.id)} t={t} />}
      {assigningToClass && <AssignTeachersModal onClose={() => setAssigningToClass(null)} cls={assigningToClass} teachers={assignableTeachers} onSave={onAssignTeachers} t={t}/>}
      {showStaffModal && (
        <StaffApprovalModal 
          onClose={() => setShowStaffModal(false)} 
          pendingTeachers={pendingTeachers} 
          activeTeachers={activeTeachers} 
          onUpdateUser={onUpdateUser} 
          onGenerateInvitationCodes={handleGenerateTeacherCodes}
          onCreatePendingTeacher={handleCreatePendingTeacher} 
          t={t} 
          orgName={org.name}
          org={org}
          invitationCodes={invitationCodes.filter(c => c.orgId === org.id && c.type === 'teacher')}
          allUsers={allUsers}
        />
      )}
      {showManageYear && (
          <ManageAcademicYearModal 
              yearToEdit={editingYear}
              onClose={() => {setShowManageYear(false); setEditingYear(null);}}
              onSave={onCreateOrUpdateAcademicYear}
              org={org}
              allAcademicYears={allAcademicYears}
              allClasses={classes}
              onCreateClasses={onCreateClasses}
              t={t}
              isInitialSetup={allAcademicYears.length === 0}
          />
      )}
      {isEditingOrg && (
        <EditOrgModal
          org={org}
          onClose={() => setIsEditingOrg(false)}
          onSave={onUpdateOrg}
          t={t}
          currentUser={user}
          allUsers={allUsers}
          onTransferOwnership={onTransferOwnership}
        />
      )}
       {isCalendarViewOpen && activeYear && (
        <PrincipalCalendarView
            user={user}
            org={org}
            activeYear={activeYear}
            classes={classes.filter(c => c.academicYearId === activeYear.id)}
            calendarEvents={calendarEvents.filter(e => e.academicYearId === activeYear.id)}
            onClose={() => setIsCalendarViewOpen(false)}
            onCreateOrUpdate={onCreateOrUpdateCalendarEvent}
            onDelete={onDeleteCalendarEvent}
            onUpdateAcademicYear={onCreateOrUpdateAcademicYear}
            t={t}
            setViewingImageUrl={setViewingImageUrl}
        />
      )}
      {showModulesModal && (
        <OrgModulesModal
          org={org}
          isOpen={showModulesModal}
          onClose={() => setShowModulesModal(false)}
          onSave={onUpdateOrg}
          mode="principal"
        />
      )}
      {showVicePrincipalsModal && (
        <VicePrincipalsModal
          onClose={() => setShowVicePrincipalsModal(false)}
          org={org}
          currentUser={user}
          allUsers={allUsers}
          onUpdateOrg={onUpdateOrg}
          onUpdateUser={onUpdateUser}
          onTransferOwnership={onTransferOwnership}
          t={t}
        />
      )}
      {showTransferOwnershipModal && onTransferOwnership && (
        <TransferOwnershipModal
          isOpen={showTransferOwnershipModal}
          onClose={() => setShowTransferOwnershipModal(false)}
          org={org}
          currentUser={user}
          allUsers={allUsers}
          onTransferOwnership={onTransferOwnership}
          t={t}
        />
      )}
      {showQAModal && (
        <SystemQADiagnosticsModal
          onClose={() => setShowQAModal(false)}
        />
      )}
    </>
  );
};
